/**
 * Input type for refactored ProjectAnalysis (NodeDto-based).
 * Replaces ProjectData: scenarios + per-scenario/model node data and lookups from useModelsData/useResultsNodes.
 */
import type { INodeDto } from "@/types/INodeDto";
import type { InstanceModel } from "@/types/LCAoM/InstanceModel";
import { createNodeMaterialKey } from "@/types/LCAoM/ModelsDataService";
import { NodeTypes } from "@/types/LCAoM/TreeBuilder";
import {
  MaterialType,
  SupplementalType,
  type Model,
  type NodeMaterialDto,
  type NodeSupplementalDto,
  type Scenario,
  type ScenarioModel,
} from "@api/apiClient";

/**
 * Extracts the parent path from a SQL Server HierarchyId string.
 * e.g. "/1/2/3/" -> "/1/2/", "/1/" -> undefined (root)
 */
export function getParentNodePath(nodePath: string | null | undefined): string | undefined {
  if (!nodePath || nodePath === "/") return undefined;
  const trimmed = nodePath.endsWith("/") ? nodePath.slice(0, -1) : nodePath;
  const parts = trimmed.split("/").filter((p) => p !== "");
  if (parts.length <= 1) return undefined;
  parts.pop();
  return "/" + parts.join("/") + "/";
}

/** Material types included in server ScenarioModel / Scenario CO2 totals (LCAoMService leaf aggregation). */
const CO2_AGGREGATION_MATERIAL_TYPES = new Set<string>([
  MaterialType.Element,
  MaterialType.Component,
  MaterialType.PrimarySupplemental,
]);

export interface ProcessableNodesOptions {
  scenarioId: string;
  nodeSupplementalLookup?: Map<string, NodeSupplementalDto>;
  nodeMaterialLookup?: Map<string, NodeMaterialDto>;
}

function isCo2AggregationMaterial(
  node: INodeDto,
  scenarioId: string,
  nodeMaterialLookup?: Map<string, NodeMaterialDto>
): boolean {
  if (!nodeMaterialLookup) return true;
  const material = nodeMaterialLookup.get(createNodeMaterialKey(scenarioId, Number(node.nodeId)));
  if (!material?.type) return true;
  return CO2_AGGREGATION_MATERIAL_TYPES.has(material.type);
}

/** NodeDto carries childCount from API/MemoryPack; base INodeDto omits it. */
function getNodeChildCount(node: INodeDto): number {
  return (node as INodeDto & { childCount?: number }).childCount ?? 0;
}

function isPrimarySupplementalNode(
  node: INodeDto,
  scenarioId: string,
  nodeSupplementalLookup?: Map<string, NodeSupplementalDto>
): boolean {
  if (!nodeSupplementalLookup) return true;
  const supplemental = nodeSupplementalLookup.get(`${scenarioId}:${node.nodeId}`);
  if (!supplemental?.type) return true;
  return supplemental.type === SupplementalType.Primary;
}

/**
 * Returns nodes that should be processed for analysis (aligned with server leaf CO2 aggregation).
 * Include: leaf elements (no child components, no child supplementals), leaf components (no child supplementals), primary supplementals only.
 * Exclude: non-leaf nodes; elements with components; elements/components with child supplementals; non-primary supplementals; Aggregate materials.
 * Accepts Iterable to allow getNodes() to return a lazy concat without allocating a combined array.
 */
export function getProcessableNodes(nodes: Iterable<INodeDto>, options?: ProcessableNodesOptions): INodeDto[] {
  const result: INodeDto[] = [];
  const elementsById = new Map<string, INodeDto>();
  const componentsById = new Map<string, INodeDto>();
  const supplementals: INodeDto[] = [];

  for (const node of nodes) {
    const id = String(node.nodeId);
    if (node.nodeType === NodeTypes.Element) {
      elementsById.set(id, node);
    } else if (node.nodeType === NodeTypes.Component) {
      componentsById.set(id, node);
    } else if (node.nodeType === NodeTypes.Supplemental) {
      supplementals.push(node);
    }
  }

  const parentPathsWithSupplementalChildren = new Set<string>();
  for (const sup of supplementals) {
    const parentPath = getParentNodePath(sup.nodePath ?? null);
    if (parentPath) parentPathsWithSupplementalChildren.add(parentPath);
  }
  const hasChildSupplementals = (nodePath: string | null | undefined): boolean =>
    !!nodePath && parentPathsWithSupplementalChildren.has(nodePath);

  for (const el of elementsById.values()) {
    if (!el) continue;
    if (getNodeChildCount(el) > 0) continue;
    if (((el as INodeDto).componentCount ?? 0) > 0) continue;
    if (hasChildSupplementals(el.nodePath ?? null)) continue;
    if (options && !isCo2AggregationMaterial(el, options.scenarioId, options.nodeMaterialLookup)) continue;
    result.push(el);
  }
  for (const comp of componentsById.values()) {
    if (getNodeChildCount(comp) > 0) continue;
    if (hasChildSupplementals(comp.nodePath ?? null)) continue;
    if (options && !isCo2AggregationMaterial(comp, options.scenarioId, options.nodeMaterialLookup)) continue;
    result.push(comp);
  }
  for (const sup of supplementals) {
    if (getNodeChildCount(sup) > 0) continue;
    if (options && !isPrimarySupplementalNode(sup, options.scenarioId, options.nodeSupplementalLookup)) continue;
    if (options && !isCo2AggregationMaterial(sup, options.scenarioId, options.nodeMaterialLookup)) continue;
    result.push(sup);
  }

  const seenNodeIds = new Set<string>();
  return result.filter((node) => {
    const id = String(node.nodeId);
    if (seenNodeIds.has(id)) return false;
    seenNodeIds.add(id);
    return true;
  });
}

/**
 * Gets in-scope total CO2 for a node from nodeMaterialLookup (NodeMaterialDto).
 * CO2 phase fields (a1A2A3, a4, totalCo2, etc.) come from NodeMaterialDto flat fields, not from NodeDto.
 */
export function getNodeInScopeTotalCo2(
  node: INodeDto,
  scenarioId: string,
  nodeMaterialLookup: Map<string, NodeMaterialDto>,
  totalCo2Fields: string[]
): number | null {
  const key = createNodeMaterialKey(scenarioId, Number(node.nodeId));
  const material = nodeMaterialLookup.get(key);
  if (!material) return null;
  if (material.type && !CO2_AGGREGATION_MATERIAL_TYPES.has(material.type)) return null;
  // NodeMaterialDto has flat CO2 fields (totalCo2, a1A2A3, a4, etc.), not co2Aggregates
  const co2Source = material as unknown as Record<string, number | undefined>;
  return totalCo2Fields.reduce<number | null>((acc, field) => {
    const val = co2Source[field];
    if (val == null) return acc;
    return (acc ?? 0) + val;
  }, null);
}

/**
 * Gets co2MaterialTypeId for a node from nodeMaterialLookup (NodeMaterialDto).
 * NodeDto does not carry co2MaterialTypeId; it comes from the material record.
 */
export function getNodeCo2MaterialTypeId(
  node: INodeDto,
  scenarioId: string,
  nodeMaterialLookup: Map<string, NodeMaterialDto>,
  emptyGuid: string
): string {
  const key = createNodeMaterialKey(scenarioId, Number(node.nodeId));
  const material = nodeMaterialLookup.get(key);
  return material?.co2MaterialTypeId ?? emptyGuid;
}

/** CO2 phase field names on ScenarioModel / NodeMaterialDto used for display and aggregation */
export const CO2_STAGE_DISPLAY_FIELDS = [
  "a1A2A3", "a4", "a5", "b1", "b2", "b3", "b4", "b5", "b6", "b7",
  "biogenic", "c1", "c2", "c3", "c4", "d", "totalCo2",
] as const;

/** @deprecated Use CO2_STAGE_DISPLAY_FIELDS */
const CO2_AGGREGATE_FIELDS = CO2_STAGE_DISPLAY_FIELDS;

const getFiniteCo2Number = (value: unknown): number | undefined => {
  if (value == null) return undefined;
  const parsed = typeof value === "number" ? value : typeof value === "string" && value.trim() !== "" ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Reads persisted CO2 phase values from a ScenarioModel (server-calculated totals).
 * Use for Results grid display — avoids client aggregation with wrong scenario materials.
 */
export function getCo2FieldsFromScenarioModel(
  scenarioModel: ScenarioModel
): Record<string, number> {
  const source = scenarioModel as unknown as Record<string, unknown>;
  const out: Record<string, number> = {};
  for (const field of CO2_STAGE_DISPLAY_FIELDS) {
    const value = getFiniteCo2Number(source[field]);
    if (value != null) {
      out[field] = value;
    }
  }
  return out;
}

/**
 * Aggregates CO2 phase values from nodeMaterialLookup for the given nodes.
 * Only use when {@link ProjectAnalysisInput.materialsLoadedScenarioIds} contains the scenarioId.
 * Returns a plain object compatible with Co2Aggregates (a1A2A3, a4, totalCo2, etc.).
 */
export function aggregateCo2FromNodes(
  nodes: readonly INodeDto[],
  scenarioId: string,
  nodeMaterialLookup: Map<string, NodeMaterialDto>,
  materialsLoadedScenarioIds?: ReadonlySet<string>
): Record<string, number> {
  if (materialsLoadedScenarioIds && !materialsLoadedScenarioIds.has(scenarioId)) {
    return {};
  }
  const out: Record<string, number> = {};
  for (const field of CO2_AGGREGATE_FIELDS) {
    let sum = 0;
    for (const node of nodes) {
      const key = createNodeMaterialKey(scenarioId, Number(node.nodeId));
      const material = nodeMaterialLookup.get(key) as unknown as Record<string, number | undefined> | undefined;
      const val = material?.[field];
      if (typeof val === "number" && Number.isFinite(val)) sum += val;
    }
    if (sum !== 0) out[field] = sum;
  }
  return out;
}

/** Per-model entry: reuses Scene/InstanceModel for nodes when present; ScenarioModel for API totals and .model. */
export interface ProjectAnalysisModelEntry {
  /** From Scene when models are loaded; undefined when only scenario data is available (grid can still show row). */
  instanceModel?: InstanceModel;
  /** ScenarioModel from API (totalCo2, percentAssigned, CO2 phase fields, .model). Always set. */
  scenarioModel: ScenarioModel;
  /** Scenario-specific Supplemental nodes (from multiScenarioNodeData) for this model. Merged with instanceModel nodes in getNodes(). */
  scenarioNodes?: INodeDto[];
}

/**
 * Nodes for this model (static from InstanceModel + scenario-specific supplementals from scenarioNodes).
 * Returns an iterable that yields both sources without allocating a concatenated array.
 */
export function getNodes(entry: ProjectAnalysisModelEntry): Iterable<INodeDto> {
  const staticNodes = (entry.instanceModel?.getAllNodes() ?? []) as INodeDto[];
  const scenarioNodes = entry.scenarioNodes ?? [];
  /** API scenarioNodes are supplemental-only; static OPFS trees already include them — avoid double-counting. */
  const excludeStaticSupplementals = scenarioNodes.length > 0;
  return {
    *[Symbol.iterator]() {
      for (const node of staticNodes) {
        if (excludeStaticSupplementals && node.nodeType === NodeTypes.Supplemental) {
          continue;
        }
        yield node;
      }
      yield* scenarioNodes;
    },
  };
}

/** Processable nodes for a model entry using server-aligned CO2 aggregation rules. */
export function getProcessableNodesForModel(
  modelEntry: ProjectAnalysisModelEntry,
  options: ProcessableNodesOptions
): INodeDto[] {
  return getProcessableNodes(getNodes(modelEntry), options);
}

/** Model for this entry (ScenarioModel.model). */
export function getModel(entry: ProjectAnalysisModelEntry): Model {
  return entry.scenarioModel.model!;
}

/** Per-scenario entry: scenario metadata + model entries for selected models */
export interface ProjectAnalysisScenarioEntry {
  scenario: Scenario;
  modelEntries: ProjectAnalysisModelEntry[];
}

/**
 * Input for refactored ProjectAnalysis. Replaces ProjectData.
 * Built from useScenarios + useResultsNodes (static) + useModelsData (nodeMaterialLookup, nodeSupplementalLookup).
 * scenarioEntries contains all scenarios (for grid display). selectedScenarioIds is the subset to run analysis for;
 * ProjectAnalysis filters by selectionManager.scenarioIds (same as selectedScenarioIds) and getSelectedModelIds(scenarioId).
 */
export interface ProjectAnalysisInput {
  projectId: string;
  /** All scenarios with their model entries (for grid display). Grid shows all; analysis only processes those in selectedScenarioIds. */
  scenarioEntries: ProjectAnalysisScenarioEntry[];
  /** Scenario IDs to run analysis for (subset of scenarioEntries; when empty, no scenario is processed) */
  selectedScenarioIds: string[];
  /** Materials by key scenarioId:nodeId (from ModelsDataService.nodeMaterials) */
  nodeMaterialLookup: Map<string, NodeMaterialDto>;
  /** Supplementals by `${scenarioId}:${nodeId}` — never keyed by nodeId alone (avoids cross-scenario bleed). */
  nodeSupplementalLookup: Map<string, NodeSupplementalDto>;
  /** Scenario IDs whose materials/supplemental nodes have been loaded for chart/analysis aggregation. */
  materialsLoadedScenarioIds: ReadonlySet<string>;
  /** Optional cad model categories (id -> label) from API; used when node-derived categories are empty so ByCategoriesResults still shows group keys */
  cadModelCategories?: Map<string, string>;
}
